import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import api, { fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import {
  PlusCircle, MoreVertical, Trash2, Play, Download, RotateCw, FilmIcon, Clapperboard,
  PenLine, BookOpen, Share2, Languages, CalendarDays, Layers, Rocket,
} from "lucide-react";

const statusColors = {
  draft:      { dot: "bg-zinc-500", label: "text-zinc-400" },
  queued:     { dot: "bg-blue-500", label: "text-blue-400" },
  processing: { dot: "bg-amber-500 animate-pulse", label: "text-amber-400" },
  completed:  { dot: "bg-emerald-500", label: "text-emerald-400" },
  failed:     { dot: "bg-red-500", label: "text-red-400" },
};

export default function Dashboard() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const nav = useNavigate();
  const { user } = useAuth();

  const load = async () => {
    try {
      const { data } = await api.get("/projects");
      setProjects(data);
    } catch (e) {
      toast.error("Failed to load projects");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 3500); // live status polling
    return () => clearInterval(t);
  }, []);

  const del = async (id) => {
    try {
      await api.delete(`/projects/${id}`);
      toast.success("Project deleted");
      setProjects((ps) => ps.filter((p) => p.id !== id));
    } catch {
      toast.error("Delete failed");
    }
  };

  const toggleSelect = (id) => {
    setSelected((s) => {
      const n = new Set(s);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  };

  const batchGenerate = async () => {
    const ids = Array.from(selected);
    if (!ids.length) return;
    try {
      await api.post("/projects/batch-generate", { project_ids: ids });
      toast.success(`Queued ${ids.length} project(s)`);
      setSelected(new Set());
      load();
    } catch {
      toast.error("Batch generate failed");
    }
  };

  return (
    <div className="p-6 md:p-10 lg:p-14 max-w-[1600px]">
      {/* Compact header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div>
          <div className="font-mono-tech text-[10px] text-amber-400 mb-2">STUDIO · DASHBOARD</div>
          <h1 className="font-heading text-3xl sm:text-4xl tracking-tighter font-bold leading-none">
            {greetForPersona(user)}
          </h1>
        </div>
        <div className="flex flex-wrap gap-3">
          {selected.size > 0 && (
            <Button
              data-testid="batch-generate-button"
              onClick={batchGenerate}
              variant="outline"
              className="bg-transparent border-white/15 hover:bg-white/5"
            >
              <RotateCw className="w-4 h-4 mr-2" strokeWidth={1.5} />
              Batch generate ({selected.size})
            </Button>
          )}
          <Button
            data-testid="new-project-button"
            onClick={() => nav("/projects/new")}
            className="bg-amber-500 hover:bg-amber-600 text-black font-semibold"
          >
            <PlusCircle className="w-4 h-4 mr-2" strokeWidth={2} />
            New video
          </Button>
        </div>
      </div>

      {/* Recommended tools — persona-based */}
      <RecommendedTools persona={user?.persona} contentGoal={user?.content_goal} />

      {/* Stat row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
        <StatCard label="TOTAL PROJECTS" value={projects.length} testid="stat-total" />
        <StatCard label="COMPLETED"     value={projects.filter((p) => p.status === "completed").length} accent testid="stat-completed" />
        <StatCard label="PROCESSING"    value={projects.filter((p) => p.status === "processing" || p.status === "queued").length} testid="stat-processing" />
        <StatCard label="DRAFTS"        value={projects.filter((p) => p.status === "draft").length} testid="stat-drafts" />
      </div>

      {/* Grid */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="h-64 bg-white/5 border border-white/5 rounded-lg animate-pulse" />
          ))}
        </div>
      ) : projects.length === 0 ? (
        <EmptyState onCreate={() => nav("/projects/new")} />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5" data-testid="project-grid">
          {projects.map((p, i) => {
            const sc = statusColors[p.status] || statusColors.draft;
            const isSel = selected.has(p.id);
            return (
              <motion.div
                key={p.id}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.04, duration: 0.4 }}
                className={`group relative bg-[#121214] border rounded-lg overflow-hidden ${
                  isSel ? "border-amber-500" : "border-white/5 hover:border-white/15"
                } transition-colors duration-300`}
                data-testid={`project-card-${p.id}`}
              >
                {/* Thumbnail */}
                <div
                  className="aspect-video bg-zinc-900 relative cursor-pointer"
                  onClick={() => nav(`/projects/${p.id}`)}
                >
                  {p.thumbnail_filename ? (
                    <img
                      src={fileUrl(`thumbnails/${p.thumbnail_filename}`)}
                      alt={p.name}
                      className="w-full h-full object-cover"
                    />
                  ) : p.photos && p.photos[0] ? (
                    <img
                      src={fileUrl(`photos/${p.id}/${p.photos[0].filename}`)}
                      alt={p.name}
                      className="w-full h-full object-cover opacity-70"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-zinc-600">
                      <FilmIcon className="w-10 h-10" strokeWidth={1} />
                    </div>
                  )}

                  {/* Progress overlay */}
                  {(p.status === "processing" || p.status === "queued") && (
                    <div className="absolute inset-0 bg-black/70 flex flex-col items-center justify-center gap-3 p-4">
                      <div className="font-mono-tech text-[10px] text-amber-400">
                        {p.status.toUpperCase()} · {p.progress ?? 0}%
                      </div>
                      <div className="w-full max-w-[200px] h-1 bg-white/10 rounded-full overflow-hidden">
                        <motion.div
                          className="h-full bg-amber-500"
                          initial={{ width: 0 }}
                          animate={{ width: `${p.progress || 5}%` }}
                          transition={{ duration: 0.5 }}
                        />
                      </div>
                      <div className="text-xs text-zinc-300 text-center">{p.progress_message}</div>
                    </div>
                  )}

                  {p.status === "completed" && (
                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 bg-black/40 transition-opacity">
                      <Play className="w-12 h-12 text-white" strokeWidth={1.5} />
                    </div>
                  )}

                  {/* Aspect badge */}
                  <div className="absolute top-3 left-3 font-mono-tech text-[9px] bg-black/70 backdrop-blur px-2 py-1 rounded text-white">
                    {p.settings?.aspect} · {p.settings?.resolution?.toUpperCase()}
                  </div>

                  {/* Priority badge (Studio+ high-priority renders) */}
                  {p.render_priority === "high" && (
                    <div
                      data-testid={`priority-badge-${p.id}`}
                      className="absolute top-10 left-3 font-mono-tech text-[9px] bg-amber-500/90 text-black backdrop-blur px-2 py-1 rounded flex items-center gap-1"
                      title="Priority render — Studio queue"
                    >
                      <Rocket className="w-3 h-3" strokeWidth={2} /> PRIORITY
                    </div>
                  )}

                  {/* Selection checkbox */}
                  <button
                    onClick={(e) => { e.stopPropagation(); toggleSelect(p.id); }}
                    className={`absolute top-3 right-3 w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                      isSel ? "bg-amber-500 border-amber-500" : "border-white/40 bg-black/40"
                    }`}
                    data-testid={`select-project-${p.id}`}
                    aria-label="Select project"
                  >
                    {isSel && <span className="w-2 h-2 bg-black rounded-sm" />}
                  </button>
                </div>

                {/* Meta */}
                <div className="p-4">
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <div className="min-w-0">
                      <div className="font-heading font-bold text-base tracking-tight truncate">{p.name}</div>
                      <div className="text-xs text-zinc-500 truncate mt-0.5">{p.address || "No address"}</div>
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button
                          data-testid={`project-menu-${p.id}`}
                          className="text-zinc-500 hover:text-white p-1"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <MoreVertical className="w-4 h-4" strokeWidth={1.5} />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent className="bg-[#1a1a1e] border-white/10">
                        {p.status === "completed" && p.video_filename && (
                          <DropdownMenuItem
                            asChild
                            data-testid={`download-${p.id}`}
                          >
                            <a
                              href={fileUrl(`videos/${p.video_filename}`)}
                              download
                              className="cursor-pointer"
                            >
                              <Download className="w-4 h-4 mr-2" strokeWidth={1.5} />
                              Download
                            </a>
                          </DropdownMenuItem>
                        )}
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <DropdownMenuItem
                              className="text-red-400 focus:text-red-300 cursor-pointer"
                              onSelect={(e) => e.preventDefault()}
                              data-testid={`delete-project-${p.id}`}
                            >
                              <Trash2 className="w-4 h-4 mr-2" strokeWidth={1.5} />
                              Delete
                            </DropdownMenuItem>
                          </AlertDialogTrigger>
                          <AlertDialogContent className="bg-[#121214] border-white/10">
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete project?</AlertDialogTitle>
                              <AlertDialogDescription className="text-zinc-400">
                                This will remove the video, photos and voice-over. This cannot be undone.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel className="bg-transparent">Cancel</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => del(p.id)}
                                className="bg-red-500 hover:bg-red-600 text-white"
                              >
                                Delete
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>

                  <div className="flex items-center gap-2 mt-3">
                    <span className={`w-1.5 h-1.5 rounded-full ${sc.dot}`} />
                    <span className={`font-mono-tech text-[9px] ${sc.label}`}>
                      {p.status.toUpperCase()}
                    </span>
                    <span className="text-zinc-700 mx-1">·</span>
                    <span className="font-mono-tech text-[9px] text-zinc-500">
                      {p.photos?.length || 0} PHOTOS
                    </span>
                    <span className="text-zinc-700 mx-1">·</span>
                    <span className="font-mono-tech text-[9px] text-zinc-500 uppercase">
                      {p.settings?.style}
                    </span>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value, accent = false, testid }) {
  return (
    <div
      data-testid={testid}
      className="bg-[#121214] border border-white/5 rounded-lg p-4 flex items-center justify-between"
    >
      <div className="font-mono-tech text-[9px] text-zinc-500">{label}</div>
      <div className={`font-heading text-3xl font-bold tracking-tighter ${accent ? "text-amber-400" : "text-white"}`}>
        {value}
      </div>
    </div>
  );
}

const PERSONA_LABEL = {
  realtor:    "Your listings, cinematic.",
  youtuber:   "Grow your channel.",
  tiktoker:   "Go viral, again.",
  influencer: "Content that converts.",
  business:   "Marketing on autopilot.",
  fun:        "Have fun creating.",
  other:      "Your creative studio.",
};

function greetForPersona(user) {
  if (!user) return "Welcome.";
  return PERSONA_LABEL[user.persona] || "Welcome back.";
}

const ALL_TOOLS = [
  { to: "/projects/new", label: "New Video",       desc: "Photos → cinematic clip", icon: PlusCircle,
    personas: ["realtor", "youtuber", "tiktoker", "influencer", "business", "fun"] },
  { to: "/ai/series",    label: "Content Series",  desc: "30 posts in one click",   icon: Layers,
    personas: ["realtor", "youtuber", "tiktoker", "influencer", "business", "fun", "other"] },
  { to: "/ai/social",    label: "Social Studio",   desc: "IG / TikTok / YT posts",  icon: Share2,
    personas: ["youtuber", "tiktoker", "influencer", "business", "fun"] },
  { to: "/ai/blog",      label: "AI Blog",         desc: "SEO articles in seconds", icon: BookOpen,
    personas: ["realtor", "business", "influencer"] },
  { to: "/ai/translate", label: "Translator",      desc: "Any text, any language",  icon: Languages,
    personas: ["realtor", "youtuber", "tiktoker", "influencer", "business", "fun", "other"] },
  { to: "/calendar",     label: "Calendar",        desc: "Plan & schedule content", icon: CalendarDays,
    personas: ["realtor", "youtuber", "tiktoker", "influencer", "business", "fun", "other"] },
  { to: "/ai/copy",      label: "AI Copy",         desc: "Listing descriptions",    icon: PenLine,
    personas: ["realtor", "business"] },
];

function RecommendedTools({ persona, contentGoal }) {
  // Rank tools: exact-persona matches first, then goal-relevant, then rest.
  const goalBoost = {
    property_video: ["/projects/new"],
    social_clips:   ["/ai/social", "/projects/new"],
    blog_articles:  ["/ai/blog", "/ai/copy"],
    ad_campaigns:   ["/ai/social", "/ai/copy"],
    everything:     [],
  };
  const boosted = new Set(goalBoost[contentGoal] || []);
  const relevant = persona
    ? ALL_TOOLS.filter((t) => t.personas.includes(persona))
    : ALL_TOOLS;
  const sorted = [...relevant].sort((a, b) => {
    const aB = boosted.has(a.to) ? 1 : 0;
    const bB = boosted.has(b.to) ? 1 : 0;
    return bB - aB;
  });
  const top = sorted.slice(0, 4);
  if (!top.length) return null;
  return (
    <div className="mb-8" data-testid="recommended-tools">
      <div className="font-mono-tech text-[10px] text-zinc-500 mb-3">
        {persona ? "RECOMMENDED · FOR · YOU" : "TOOLS"}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {top.map((t) => {
          const Icon = t.icon;
          return (
            <Link
              key={t.to}
              to={t.to}
              data-testid={`recommended-${t.to.replace(/\//g, "-")}`}
              className="group bg-[#121214] border border-white/5 hover:border-amber-500/40 hover:bg-white/[0.03] rounded-lg p-4 transition-colors"
            >
              <div className="flex items-center gap-3 mb-2">
                <div className="w-9 h-9 rounded-md bg-amber-500/10 border border-amber-500/20 flex items-center justify-center group-hover:bg-amber-500/20 transition-colors">
                  <Icon className="w-4 h-4 text-amber-400" strokeWidth={1.5} />
                </div>
                <div className="font-heading font-bold text-sm tracking-tight">{t.label}</div>
              </div>
              <div className="text-xs text-zinc-500">{t.desc}</div>
            </Link>
          );
        })}
      </div>
    </div>
  );
}

function EmptyState({ onCreate }) {
  return (
    <div className="border border-dashed border-white/10 rounded-lg p-12 text-center">
      <div className="w-16 h-16 rounded-full bg-amber-500/10 border border-amber-500/30 flex items-center justify-center mx-auto mb-6">
        <Clapperboard className="w-8 h-8 text-amber-400" strokeWidth={1.5} />
      </div>
      <h3 className="font-heading text-2xl font-bold tracking-tight">Start your first video</h3>
      <p className="text-zinc-400 mt-2 max-w-md mx-auto text-sm">
        Upload property photos, pick a style, and let the studio produce a cinematic promotional video.
      </p>
      <Button
        data-testid="empty-create-button"
        onClick={onCreate}
        className="mt-6 bg-amber-500 hover:bg-amber-600 text-black font-semibold"
      >
        <PlusCircle className="w-4 h-4 mr-2" strokeWidth={2} />
        Create your first video
      </Button>
    </div>
  );
}
